import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';
import { toast } from '../components/ui/Toast';
import type { CreateMaterialInput, UpdateMaterialInput } from '@shared/validation/material-schemas';

// ── DTOs ──
export interface MaterialDTO {
  id: string;
  nombre: string;
  sku: string | null;
  unidad: string;
  precio_unitario: number;
  categoria: string;
  presentacion: string | null;
  rendimiento: number | null;
  rendimiento_unidad: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface MaterialDetailDTO extends MaterialDTO {
  used_in_recipes_count: number;
}

interface MaterialsResponse {
  data: MaterialDTO[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

interface MaterialFilters {
  search?: string;
  categoria?: string;
  notCategoria?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

// ── List ──
export function useMaterials(filters?: MaterialFilters) {
  return useQuery({
    queryKey: queryKeys.materials.list(filters as Record<string, unknown>),
    queryFn: async () => {
      const response = await api.get<MaterialsResponse>('/materials', {
        params: filters,
      });
      return response.data;
    },
  });
}

// ── Detail ──
export function useMaterial(id: string) {
  return useQuery({
    queryKey: queryKeys.materials.detail(id),
    queryFn: async () => {
      const response = await api.get<{ data: MaterialDetailDTO }>(`/materials/${id}`);
      return response.data.data;
    },
    enabled: !!id,
  });
}

// ── Create ──
export function useCreateMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateMaterialInput) => {
      const response = await api.post<{ data: MaterialDTO }>('/materials', data);
      return response.data.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
      toast('success', `Material "${data.nombre}" creado exitosamente`);
    },
  });
}

// ── Update ──
export function useUpdateMaterial() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: UpdateMaterialInput }) => {
      const response = await api.put<{ data: MaterialDetailDTO }>(`/materials/${id}`, data);
      return response.data.data;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.materials.detail(variables.id) });
      toast('success', 'Material actualizado');
    },
  });
}

// ── Delete (soft) ──
export function useDeleteMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const response = await api.delete(`/materials/${id}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.materials.all });
      toast('success', 'Material desactivado');
    },
  });
}
